import type { StrapiId } from "@investhinks/api-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type { StrapiId };

type LoadItemFn<T> = (id: StrapiId, params?: Record<string, unknown>) => Promise<T | [T | null] | null | undefined>;
type SubmitItemFn<T> = (item: T) => Promise<T | [T | null] | null | undefined>;
type DeleteItemFn = (item: { id?: StrapiId }) => Promise<unknown>;

export interface UseStrapiOneOptions<T = unknown> {
  id?: StrapiId;
  params?: Record<string, unknown>;
  loadItem?: LoadItemFn<T>;
  submitItem?: SubmitItemFn<T>;
  deleteItem?: DeleteItemFn;
  onLoadError?: (error: unknown) => void;
}

export interface UseStrapiOneReturn<T = unknown> {
  item: T | null;
  setItem: React.Dispatch<React.SetStateAction<T | null>>;
  isLoading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  load: (newId?: StrapiId) => Promise<T | undefined>;
  update: (newItem?: T) => Promise<T | undefined>;
  remove: () => Promise<unknown>;
}

/**
 * Hook for fetching and managing a single item from Strapi API
 */
function useStrapiOne<T = unknown>(options: UseStrapiOneOptions<T> = {}): UseStrapiOneReturn<T> {
  const { id, params, loadItem, submitItem, deleteItem, onLoadError } = options;

  const [item, setItem] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);
  const prevIdRef = useRef<StrapiId | undefined>(id);

  // Stabilize params to prevent infinite re-renders when params object is recreated
  const paramsKey = JSON.stringify(params);
  const stableParams = useMemo(() => params, [paramsKey]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Load item
  const load = useCallback(
    async (newId?: StrapiId): Promise<T | undefined> => {
      const targetId = newId ?? id;
      if (!targetId || !loadItem) return undefined;

      setIsLoading(true);
      try {
        const result = await loadItem(targetId, stableParams);

        if (!mountedRef.current) return undefined;

        // Handle [item] tuple or just item
        if (Array.isArray(result)) {
          const loadedItem = result[0] || null;
          setItem(loadedItem);
          return loadedItem || undefined;
        } else {
          setItem(result || null);
          return result || undefined;
        }
      } catch (error: unknown) {
        if (!mountedRef.current) return undefined;
        if (onLoadError) {
          onLoadError(error);
        } else {
          console.error("useStrapiOne load error:", error);
        }
        return undefined;
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [id, stableParams, loadItem, onLoadError]
  );

  // Update item
  const update = useCallback(
    async (newItem?: T): Promise<T | undefined> => {
      if (!submitItem) return undefined;

      const itemToSubmit = newItem ?? item;
      if (!itemToSubmit) return undefined;

      setIsLoading(true);
      try {
        const result = await submitItem(itemToSubmit);

        if (!mountedRef.current) return undefined;

        // Handle [item] tuple or just item
        const updatedItem = Array.isArray(result) ? result[0] : result;
        if (updatedItem) {
          setItem(updatedItem);
        }
        return updatedItem || undefined;
      } catch (error: unknown) {
        if (!mountedRef.current) return undefined;
        throw error; // Let caller handle submit errors
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [item, submitItem]
  );

  // Delete item
  const remove = useCallback(async (): Promise<unknown> => {
    if (!deleteItem || !item) return undefined;

    setIsLoading(true);
    try {
      const result = await deleteItem(item);

      if (!mountedRef.current) return undefined;

      setItem(null);
      return result;
    } catch (error: unknown) {
      if (!mountedRef.current) return undefined;
      throw error; // Let caller handle delete errors
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [item, deleteItem]);

  // Auto-load when id changes
  useEffect(() => {
    const idChanged = prevIdRef.current !== id;
    prevIdRef.current = id;

    if (id) {
      load();
    } else if (idChanged) {
      // Only reset to null when id actually changes to undefined/null,
      // not when load reference changes (which would reset manual setItem calls)
      setItem(null);
    }
  }, [id, load]);

  return {
    item,
    setItem,
    isLoading,
    setLoading: setIsLoading,
    load,
    update,
    remove,
  };
}

export default useStrapiOne;
